// src/features/dashboard/components/QuickActions.tsx
import {
  UserPlus,
  PackagePlus,
  WalletCards,
  Boxes,
  ServerCog,
  BarChart3,
  ArrowLeft,
} from 'lucide-react';
import { GlassCard } from '../../../app/components/ui/GlassCard';
import type { QuickAction, QuickActionKey } from '../types/dashboard.types';

interface QuickActionsProps {
  actions: QuickAction[];
  onNavigate?: (action: QuickAction) => void;
}

const ICON_MAP: Record<QuickActionKey, typeof UserPlus> = {
  'create-customer': UserPlus,
  'create-order': PackagePlus,
  wallet: WalletCards,
  products: Boxes,
  services: ServerCog,
  reports: BarChart3,
};

/**
 * Dashboard V3 Phase 2: shares the "Business Operations" section with
 * ServiceSummary, so its internal header uses the same `flex flex-col
 * gap-*` rhythm as that widget's heading for a consistent vertical
 * cadence between the two panels. `h-full` lets it match ServiceSummary's
 * height when the pair sits side by side on wide viewports.
 *
 * Phase 5: header now uses the shared `.vania-panel-title` treatment (the
 * same title styling as the Activity panels) instead of a one-off inline
 * class, and each tile's title/description pair uses the shared
 * `.vania-quick-action-*` typography hooks so hierarchy and hover/focus
 * behavior match the rest of the dashboard. No change to props, actions,
 * or navigation behavior.
 */
export function QuickActions({ actions, onNavigate }: QuickActionsProps) {
  return (
    <GlassCard className="flex h-full flex-col gap-4" data-animate="fade-in">
      <h3 className="vania-panel-title px-1">دسترسی سریع</h3>

      <div className="grid vania-quick-actions-grid">
        {actions.map((action) => {
          const Icon = ICON_MAP[action.key];
          return (
            <button
              key={action.id}
              type="button"
              onClick={() => onNavigate?.(action)}
              className="vania-quick-action group relative min-w-0 overflow-hidden"
            >
              <span className={`vania-icon-badge vania-icon-badge--${action.accent}`}>
                <Icon size={18} strokeWidth={2.2} />
              </span>
              {/* pe-4 reserves just enough room for the arrow, which is now
                  positioned absolutely (see below) instead of sitting in the
                  flex flow. Keeping it in-flow was permanently reserving
                  ~27px per tile even while invisible (opacity-0), which at
                  narrow track widths left too little room for the title to
                  wrap and pushed it outside the tile. */}
              <span className="flex-1 min-w-0 pe-4 text-start">
                <span className="vania-quick-action-title line-clamp-2 min-w-0 [overflow-wrap:anywhere] break-words text-sm font-semibold leading-snug text-[var(--vania-text-primary,#f5f6fa)]">
                  {action.title}
                </span>
                <span className="vania-quick-action-description block truncate text-xs text-[var(--vania-text-tertiary,#6b7280)]">
                  {action.description}
                </span>
              </span>
              <ArrowLeft
                size={15}
                className="absolute end-3 top-1/2 -translate-y-1/2 shrink-0 text-[var(--vania-text-tertiary,#6b7280)] opacity-0 -translate-x-1 rtl:translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200"
              />
            </button>
          );
        })}
      </div>
    </GlassCard>
  );
}
